import { cn } from "@/lib/utils"
import CareProviderTag from "@/modules/care-provider/components/care-provider-tag"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { isInsuranceCareProviderListMember } from "@/modules/care-provider/utils/is-insurance-care-provider-list-member"
import { DoctorNetworkRole } from "@/types/__generated__/union-types"

interface IndicatorProps {
  role?: DoctorNetworkRole
  defaultMedgatePartnerNetworkAllowed?: boolean
  isMedgatePartnerNetworkMember?: boolean | null
  isTakesNewPatients?: boolean | null
  insuranceCareProviderListIds?: ReadonlyArray<string> | null
  applicableInsuranceCareProviderListIds?: ApplicableInsuranceCareProviderListIds
  className?: string
}

const getGatekeeperPartnerNetworkAndAvailabilityIndicator = ({
  role,
  defaultMedgatePartnerNetworkAllowed,
  isMedgatePartnerNetworkMember,
  isTakesNewPatients,
}: IndicatorProps) => {
  if (role === "GATEKEEPER") {
    return (
      <>
        <CareProviderTag.GateKeeper />
        {isMedgatePartnerNetworkMember && <CareProviderTag.MedgatePartnerNetwork />}
      </>
    )
  } else if (isMedgatePartnerNetworkMember && !isTakesNewPatients) {
    return <CareProviderTag.NoNewPatients />
  } else if (!isTakesNewPatients) {
    return <CareProviderTag.NoNewPatients />
  } else if (isMedgatePartnerNetworkMember) {
    return <CareProviderTag.MedgatePartnerNetwork />
  } else if (defaultMedgatePartnerNetworkAllowed) {
    return <CareProviderTag.OnlyMPNAllowed />
  }

  return null
}

const getCareProviderIndicator = ({
  insuranceCareProviderListIds,
  applicableInsuranceCareProviderListIds,
  className,
  ...indicatorProps
}: IndicatorProps) => {
  const isApplicableInsuranceCareProviderListMember = isInsuranceCareProviderListMember(
    insuranceCareProviderListIds,
    applicableInsuranceCareProviderListIds,
  )
  const gatekeeperPartnerNetworkAndAvailabilityIndicator =
    getGatekeeperPartnerNetworkAndAvailabilityIndicator(indicatorProps)

  if (!gatekeeperPartnerNetworkAndAvailabilityIndicator && !isApplicableInsuranceCareProviderListMember) {
    return null
  }

  return (
    <div className={cn("flex flex-wrap justify-end gap-1", className)} data-testid="care-provider-indicator">
      {gatekeeperPartnerNetworkAndAvailabilityIndicator}
      {isApplicableInsuranceCareProviderListMember && <CareProviderTag.InsurerNetwork />}
    </div>
  )
}

export { getCareProviderIndicator }
